/**
 * Разделы внутреннего портала: единый источник для сайдбара и плиток «Рабочего стола».
 *
 * Зачем один конфиг на два места: до редизайна список ссылок был захардкожен в
 * `Home.tsx`, а навигации не было вовсе. Теперь пункты рендерятся дважды (меню +
 * плитки), и разъехавшиеся правила видимости означали бы, что раздел виден в меню,
 * но пропал с рабочего стола. Поэтому маршрут и условие доступа описаны ровно раз.
 *
 * ВАЖНО про права: это слой ПРЕДСТАВЛЕНИЯ. Источник истины — серверная авторизация;
 * скрытая плитка не защищает маршрут. Условия ниже дословно повторяют те, что были
 * в `Home.tsx` до редизайна, — редизайн не расширяет и не сужает видимость.
 */

/** Роль сотрудника (значение `user.role`). Клиент сюда не попадает. */
export type StaffRole = 'admin' | 'operator' | 'senior_manager' | 'manager';

export type SectionId =
  | 'news' | 'orders' | 'counterparties' | 'ar' | 'stock'
  | 'commercial-terms' | 'sales' | 'shipped' | 'plan-fact' | 'pnl' | 'clients'
  | 'pending-counterparties' | 'ai' | 'card41' | 'reconcile';

/**
 * Группа раздела в меню (правка Artem 16.09.2026).
 *
 * До неё разделов было два: «Работа» и «Администрирование», и в «Работу»
 * попадало всё подряд — от чтения новостей до управленческих отчётов. Теперь
 * группа задаётся у раздела, а меню и плитки рисуют её одинаково.
 *
 * - `news` — лента для чтения, стоит над всеми группами и без заголовка;
 * - `work` — ежедневные действия с клиентами и товаром;
 * - `analytics` — отчёты и сводки, которые смотрят, а не заполняют.
 */
export type SectionGroup = 'news' | 'work' | 'analytics';

export type Section = {
  id: SectionId;
  to: string;
  label: string;
  group: SectionGroup;
  /** Короткое пояснение назначения — подпись под названием на плитке. */
  description: string;
  /**
   * Укороченная подпись для сайдбара. Задаётся только там, где полное название
   * переносится на две строки: у админа 23 пункта, и три двухстрочных не давали
   * меню поместиться на экран без прокрутки. На плитках и в заголовках экранов
   * используется полное `label`.
   */
  navLabel?: string;
};

export type AdminSection = {
  id: string;
  to: string;
  label: string;
};

/**
 * Рабочие разделы. Видны всем сотрудникам, кроме отмеченных отдельными предикатами
 * ниже (`clients`, `pending-counterparties`).
 *
 * Маршруты обязаны совпадать с `App.tsx` — они не менялись при редизайне.
 */
export const WORK_SECTIONS: Section[] = [
  { id: 'news', group: 'news', to: '/news', label: 'Новости', description: 'Отраслевые события и обновления' },
  { id: 'orders', group: 'work', to: '/orders', label: 'Заказы', description: 'Создание и управление заказами' },
  { id: 'counterparties', group: 'work', to: '/counterparties', label: 'Контрагенты', description: 'Организации и реквизиты' },
  { id: 'clients', group: 'work', to: '/clients', label: 'Клиенты', description: 'Клиентские кабинеты' },
  { id: 'commercial-terms', group: 'work', to: '/admin/commercial-terms', label: 'Коммерческие условия', description: 'Цены и договорные условия', navLabel: 'Комм. условия' },
  { id: 'stock', group: 'work', to: '/stock', label: 'Остатки на складах', description: 'Доступность товаров', navLabel: 'Остатки' },
  { id: 'ar', group: 'work', to: '/ar', label: 'Дебиторская задолженность', description: 'Контроль взаиморасчётов', navLabel: 'Дебиторка' },
  { id: 'shipped', group: 'analytics', to: '/admin/shipped', label: 'Отгруженные заказы', description: 'История и статусы отгрузки' },
  { id: 'sales', group: 'analytics', to: '/admin/sales', label: 'Продажи клиентов', description: 'Продажи и отчётность' },
  { id: 'plan-fact', group: 'analytics', to: '/admin/plan-fact', label: 'План-Факт', description: 'Выполнение плана продаж' },
  // Раздел переименован из «P&L» в «Проект продажи» (правка Artem 17.09.2026);
  // id, маршрут и права остались прежними.
  { id: 'pnl', group: 'analytics', to: '/admin/pnl', label: 'Проект продажи', description: 'Доходы, расходы и результат по месяцам' },
  // ИИ аналитик (09.10.2026): виден только при флаге доступа из /api/ai/access — список
  // разрешённых аккаунтов ведёт сервер (AI_ASSISTANT_USERS), он же отвечает 403 остальным.
  { id: 'ai', group: 'analytics', to: '/ai', label: 'ИИ аналитик', description: 'Вопросы о продажах обычными словами' },
  // Карточка счёта 41 (27.10.2026): виден по флагу из /api/admin/card41/access — admin или список
  // CARD41_USERS на сервере (пилоты Artem и Казорин); сервер отвечает 403 остальным.
  { id: 'card41', group: 'analytics', to: '/admin/card41', label: 'Сверка', description: 'Итоги периода, баланс товара, сверка с 1С' },
  // Сверка с 1С (28.10.2026): отдельный раздел скрыт по решению Artem 28.10.2026 — сверка стала вкладкой страницы «Карточка 41»
  // (/admin/card41?tab=reconcile). Вернуть пункт меню — раскомментировать строку ниже и тесты состава разделов.
  // { id: 'reconcile', group: 'analytics', to: '/admin/reconcile', label: 'Сверка с 1С', description: 'Отгрузки портала против продаж 1С: итоги и расхождения' },
];

/** Заголовки групп — одни и те же в меню и на «Рабочем столе». */
export const GROUP_LABEL: Record<SectionGroup, string> = {
  news: 'Новости',
  work: 'Работа',
  analytics: 'Аналитика',
};

/** Разделы одной группы; порядок наследуется от WORK_SECTIONS. */
export function sectionsOfGroup(sections: Section[], group: SectionGroup): Section[] {
  return sections.filter((s) => s.group === group);
}

/**
 * Справочники. Целиком admin-only — в `Home.tsx` блок рендерился под
 * `u.role === 'admin'`, это условие сохранено.
 *
 * Подпись «Поставщики» короче маршрутного смысла («Поставщики (для договоров)»),
 * но остаётся однозначной: раздел один, спутать не с чем.
 */
export const CATALOG_SECTIONS: AdminSection[] = [
  { id: 'products', to: '/admin/products', label: 'Товары' },
  { id: 'product-groups', to: '/admin/product-groups', label: 'Товарные группы' },
  { id: 'producers', to: '/admin/producers', label: 'Производители' },
  { id: 'legal-entities', to: '/admin/legal-entities', label: 'Юрлица' },
  { id: 'suppliers', to: '/admin/suppliers', label: 'Поставщики' },
  { id: 'carrier', to: '/admin/catalogs/carrier', label: 'Перевозчики' },
  { id: 'vehicle', to: '/admin/catalogs/vehicle', label: 'Машины' },
  { id: 'driver', to: '/admin/catalogs/driver', label: 'Водители' },
  { id: 'extra_service', to: '/admin/catalogs/extra_service', label: 'Доп. услуги' },
  { id: 'order_type', to: '/admin/catalogs/order_type', label: 'Типы заявок' },
  { id: 'warehouse', to: '/admin/catalogs/warehouse', label: 'Склады' },
];

/**
 * Системные разделы: управление доступом, а не данными. «Пользователи» уехали
 * сюда из справочников (правка Artem 16.09.2026) — завести пользователя и
 * завести марку машины это разные по смыслу действия.
 */
export const SYSTEM_SECTIONS: AdminSection[] = [
  { id: 'users', to: '/admin/users', label: 'Пользователи' },
];

/** Совместимость: прежний общий список = справочники плюс системные. */
export const ADMIN_SECTIONS: AdminSection[] = [...CATALOG_SECTIONS, ...SYSTEM_SECTIONS];

/**
 * Раздел «Клиенты» — только manager/senior_manager/admin.
 * Дословно из `Home.tsx`: `['manager','senior_manager','admin'].includes(u.role)`.
 */
export function canSeeClients(role: string): boolean {
  return ['manager', 'senior_manager', 'admin'].includes(role);
}

/** Справочники — admin и genadmin (условие из `Home.tsx`: `u.role === 'admin'`). */
export function canSeeAdminSections(role: string): boolean {
  return role === 'admin' || role === 'genadmin';
}

/** «План-Факт» — admin, senior_manager и genadmin (управленческий отчёт). */
export function canSeePlanFact(role: string): boolean {
  return role === 'admin' || role === 'senior_manager' || role === 'genadmin';
}

/** «P&L» — только admin и genadmin (senior_manager закрыт по решению Artem 17.09.2026). */
export function canSeePnl(role: string): boolean {
  return role === 'admin' || role === 'genadmin';
}

/**
 * «Заявки на контрагентов» — только admin И только когда заявки реально есть.
 * До редизайна пункт так же появлялся лишь при `pendingCp > 0`.
 */
export function canSeePendingCounterparties(role: string, pendingCount: number): boolean {
  return (role === 'admin' || role === 'genadmin') && pendingCount > 0;
}

/**
 * Рабочие разделы, доступные роли. Порядок сохраняется — он задан в WORK_SECTIONS.
 * `pending-counterparties` сюда не входит: у него отдельное условие со счётчиком,
 * его добавляет вызывающий код.
 */
export function visibleWorkSections(role: string, aiEnabled = false, card41Enabled = false): Section[] {
  return WORK_SECTIONS.filter((s) => {
    if (s.id === 'ai') return aiEnabled;
    if (s.id === 'card41' || s.id === 'reconcile') return card41Enabled;
    if (s.id === 'clients') return canSeeClients(role);
    if (s.id === 'plan-fact') return canSeePlanFact(role);
    if (s.id === 'pnl') return canSeePnl(role);
    return true;
  });
}

/** Справочники, доступные роли: либо все 11, либо ни одного. */
export function visibleCatalogSections(role: string): AdminSection[] {
  return canSeeAdminSections(role) ? CATALOG_SECTIONS : [];
}

/** Системные разделы — по тому же праву, что и справочники. */
export function visibleSystemSections(role: string): AdminSection[] {
  return canSeeAdminSections(role) ? SYSTEM_SECTIONS : [];
}

/** Совместимость: общий список для поиска и плиток «Рабочего стола». */
export function visibleAdminSections(role: string): AdminSection[] {
  return canSeeAdminSections(role) ? ADMIN_SECTIONS : [];
}

/**
 * Локальный фильтр разделов по подстроке. Ищет по названию и описанию,
 * регистр и лишние пробелы игнорируются.
 *
 * Фильтрует ТОЛЬКО то, что уже отдано вызывающим кодом, — то есть скрытый по правам
 * раздел не может «всплыть» через поиск.
 */
export function matchesQuery(
  item: { label: string; description?: string; navLabel?: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    item.label.toLowerCase().includes(q) ||
    // Ищем и по короткой подписи из меню: пользователь видит в сайдбаре
    // «Дебиторка» и логично набирает именно это слово.
    (item.navLabel?.toLowerCase().includes(q) ?? false) ||
    (item.description?.toLowerCase().includes(q) ?? false)
  );
}
